import React, { useEffect } from "react";
import PropTypes from "prop-types";
// @mui
import {
  Stack,
  Drawer,
  Divider,
  Typography,
  TextField,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";

// components
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";
import {
  useCreateGoogleAnalyticsSectionMutation,
  useUpdateGoogleAnalyticsSectionMutation,
} from "../../../redux/services/googleAnalyticsSection.service";
import { useGetRolesQuery } from "../../../redux/services/role.service";
import { useGetAuthUsersQuery } from "../../../redux/services/authUser.service";
import Scrollbar from "../../../components/scrollbar";

const SectionSchema = yup.object().shape({
  graphName: yup.string().required("Graph name is required"),
  roleIds: yup.array().of(yup.string()),
  authUserIds: yup.array().of(yup.string()),
});

GoogleAnalyticsSectionForm.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  edit: PropTypes.bool,
  openRow: PropTypes.object,
};

export default function GoogleAnalyticsSectionForm({
  open,
  onClose,
  edit,
  openRow,
}) {
  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    resolver: yupResolver(SectionSchema),
    defaultValues: {
      graphName: "",
      roleIds: [],
      authUserIds: [],
    },
  });

  const [createSection, { isLoading }] =
    useCreateGoogleAnalyticsSectionMutation();
  const [updateSection, { isLoading: isUpdateLoading }] =
    useUpdateGoogleAnalyticsSectionMutation();

  const { data: rolesData, isLoading: isLoadingRoles } = useGetRolesQuery({});
  const { data: authUsersData, isLoading: isLoadingAuthUsers } =
    useGetAuthUsersQuery({ isAuthAllInfo: true });

  const handleClick = async (values) => {
    // Prepare payload according to DTO - always send roleIds and authUserIds (empty array if none selected)
    const payload = {
      graphName: values.graphName,
      roleIds: values.roleIds || [],
      authUserIds: values.authUserIds || [],
    };

    let resultAction;
    if (edit) {
      resultAction = await updateSection({ ...payload, id: openRow.id });
    } else {
      resultAction = await createSection(payload);
    }
    if (resultAction?.data?.success) {
      onClose();
      reset();
    }
  };

  useEffect(() => {
    if (edit && openRow) {
      setValue("graphName", openRow.graphName);
      setValue("roleIds", openRow.roleIds || []);
      setValue("authUserIds", openRow.authUserIds || []);
    } else {
      reset({
        graphName: "",
        roleIds: [],
        authUserIds: [],
      });
    }
  }, [edit, reset, openRow, setValue, open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 480, border: "none", overflow: "hidden" },
      }}
    >
      <Scrollbar>
        <Stack spacing={3} sx={{ p: 3 }}>
          <div>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Typography variant="subtitle1">
                {edit ? "Edit" : "Create"} Google Analytics Section
              </Typography>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </Box>
            <Divider />
          </div>
          <form onSubmit={handleSubmit(handleClick)}>
            <Stack spacing={3}>
              <TextField
                name="graphName"
                label="Graph Name"
                {...register("graphName", { required: true })}
                error={Boolean(errors.graphName)}
                helperText={
                  errors.graphName?.message ||
                  "Must be one of: graph1, graph2, graph3, graph4, graph5"
                }
                disabled={edit}
                inputProps={{ "data-testid": "graphName" }}
                placeholder="graph1, graph2, graph3, graph4, or graph5"
              />

              <FormControl fullWidth>
                <InputLabel id="roleIds-label">Roles</InputLabel>
                <Controller
                  name="roleIds"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="roleIds-label"
                      label="Roles"
                      multiple
                      input={<OutlinedInput label="Roles" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const role = rolesData?.data?.find(
                              (r) => r.id === value,
                            );
                            return (
                              <Chip key={value} label={role?.name || value} />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {isLoadingRoles ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        rolesData?.data?.map((role) => (
                          <MenuItem key={role.id} value={role.id}>
                            <Checkbox checked={field.value.includes(role.id)} />
                            <ListItemText primary={role.name} />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  )}
                />
              </FormControl>

              <FormControl fullWidth>
                <InputLabel id="authUserIds-label">Auth Users</InputLabel>
                <Controller
                  name="authUserIds"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="authUserIds-label"
                      label="Auth Users"
                      multiple
                      input={<OutlinedInput label="Auth Users" />}
                      renderValue={(selected) => (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {selected.map((value) => {
                            const user = authUsersData?.data?.find(
                              (u) => u.id === value,
                            );
                            return (
                              <Chip
                                key={value}
                                label={user?.email || value}
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {isLoadingAuthUsers ? (
                        <MenuItem disabled>Loading...</MenuItem>
                      ) : (
                        authUsersData?.data?.map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            <Checkbox checked={field.value.includes(user.id)} />
                            <ListItemText
                              primary={user.email}
                              secondary={`${user.firstName || ""} ${
                                user.lastName || ""
                              }`}
                            />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  )}
                />
              </FormControl>

              <LoadingButton
                type="submit"
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                loading={edit ? isUpdateLoading : isLoading}
                data-testid="submitBtn"
              >
                Submit
              </LoadingButton>
            </Stack>
          </form>
        </Stack>
      </Scrollbar>
    </Drawer>
  );
}
